import { useEffect, useMemo, useState } from 'react';

import { useConfigSlice } from '../../../store/slices/config/selectors';
import { AppManifest } from '../../appManifest/AppManifest';
// import { AppManifest } from '../../appManifest/AppManifest';
import { FeatureService, FeatureServiceState } from '../FeatureService';
import { JSONFetcher } from '../fetchers/JSONFetcher';
import { PMSFetcher } from '../fetchers/PMSFetcher';
import type { FeatureServiceFetcher } from '../fetchers/types';
import { FeatureLoggingMiddleware } from '../logging/FeatureLoggingMiddleware';

/**
 * Hook to initialise the FeatureService.
 *
 * To be used only once.
 * */
export const useFeatureServiceConnected = (featureConfig?: string | string[]): ReturnType<typeof useFeatureService> => {
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const baseUrl = useConfigSlice.baseUrl();
  const casinoId = useConfigSlice.session()?.casinoId;

  return useFeatureService(JSESSIONID, baseUrl, casinoId, null, featureConfig);
};

export const useFeatureServiceForTableConnected = (
  featureConfig?: string | string[]
): ReturnType<typeof useFeatureService> => {
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const baseUrl = useConfigSlice.baseUrl();
  const casinoId = useConfigSlice.session()?.casinoId;
  const tableId = useConfigSlice.tableId();

  // If tableId in not set, we pass it as undefined, meaning that we still need to wait for it to be set
  // TODO: change selectors to return undefined instead of null when tableId is still pending
  return useFeatureService(JSESSIONID, baseUrl, casinoId, tableId ?? undefined, featureConfig);
};

export const useFeatureService = (
  JSESSIONID: string | null,
  baseUrl: string | null,
  casinoId: string | undefined,
  tableId: string | null | undefined,
  featureConfig?: string | string[]
): { isReady: boolean } => {
  const isReady = useFeatureServiceReady();

  useEffect(() => {
    if (JSESSIONID && baseUrl && casinoId && tableId !== undefined) {
      let fetcher: FeatureServiceFetcher;

      const usePMS = AppManifest.getFlag('usePMS');
      const logPMSFeatures = AppManifest.getFlag('logPMSFeatures');

      const jsonFetcher = new JSONFetcher(casinoId, featureConfig);
      const pmsFetcher = new PMSFetcher(JSESSIONID, baseUrl, tableId ?? undefined);

      const fetcherToUse = usePMS ? pmsFetcher : jsonFetcher;

      if (logPMSFeatures) {
        fetcher = new FeatureLoggingMiddleware(jsonFetcher, pmsFetcher, {
          use: fetcherToUse,
        });
      } else {
        fetcher = fetcherToUse;
      }

      FeatureService.init(fetcher);
    }
  }, [JSESSIONID, baseUrl, casinoId, tableId, featureConfig]);

  return useMemo(() => {
    return {
      isReady,
    };
  }, [isReady]);
};

export const useFeatureServiceReady = (): boolean => {
  const [ready, setReady] = useState<boolean>(FeatureService.state === FeatureServiceState.READY);

  useEffect(() => {
    return FeatureService.addEventListener('ready', (): void => {
      setReady(FeatureService.state === FeatureServiceState.READY);
    });
  }, []);

  return ready;
};

export const useFeature = (name: string): boolean => {
  const [feature, setFeature] = useState<boolean>(FeatureService.getFeature(name) ?? false);

  useEffect(() => {
    setFeature(FeatureService.getFeature(name) ?? false);
    return FeatureService.addEventListener('updated', (): void => {
      setFeature(FeatureService.getFeature(name) ?? false);
    });
  }, [name]);

  return feature;
};

export const useFeatureValue = (name: string): string | boolean | null | undefined => {
  const [feature, setFeature] = useState<boolean | string | null | undefined>(FeatureService.getFeatureValue(name));

  useEffect(() => {
    setFeature(FeatureService.getFeatureValue(name));
    return FeatureService.addEventListener('updated', (): void => {
      setFeature(FeatureService.getFeatureValue(name));
    });
  }, [name]);

  return feature;
};
